"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Phone } from "lucide-react";
import { useEffect, useRef, useState } from "react";
export default function Hero() {
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: taglineRef,
    isVisible: taglineVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  const imageRef = useRef<HTMLDivElement>(null);
  const [parallaxOffset, setParallaxOffset] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (imageRef.current) {
        const rect = imageRef.current.getBoundingClientRect();
        const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
        setParallaxOffset(scrollProgress * 30);
      }
    };
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!mediaQuery.matches) {
      window.addEventListener("scroll", handleScroll);
      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);
  return <section id="hero" className="relative bg-background overflow-hidden">
      {}
      <div className="absolute inset-0 pointer-events-none">
        <svg className="absolute top-0 right-0 w-[600px] h-[600px] opacity-[0.03]" viewBox="0 0 600 600" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M300 50C450 50 550 150 550 300C550 450 450 550 300 550C150 550 50 450 50 300C50 150 150 50 300 50Z" fill="var(--color-primary)" />
        </svg>
      </div>

      <div className="container mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {}
          <div className="space-y-6 lg:space-y-8">
            <div ref={headlineRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] ${headlineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
              <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tight leading-tight text-foreground">
                Compassionate ABA Therapy for Your Child's Success
              </h1>
            </div>

            <div ref={taglineRef} className={`transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-100 ${taglineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
              <p className="text-lg lg:text-xl text-muted leading-relaxed">
                Evidence-based Applied Behavior Analysis services tailored to children and adolescents with autism and developmental needs. Bilingual support in English and Spanish.
              </p>
            </div>

            <div ref={ctaRef} className={`flex flex-col sm:flex-row gap-4 pt-4 transition-all duration-[var(--duration-medium)] ease-[var(--ease-smooth)] delay-200 ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"}`}>
              <Button asChild size="lg" className="btn-primary shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98] transition-all duration-[var(--duration-fast)]">
                <Link to="/GetStartedPage">Request Services</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="shadow-soft hover:shadow-medium hover:scale-[1.02] active:scale-[0.98] transition-all duration-[var(--duration-fast)]">
                <Link to="tel:+13051234567">
                  <Phone className="size-5" />
                  Call Us
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div ref={imageRef} className="relative" style={{
          transform: `translateY(${parallaxOffset}px)`,
          transition: "transform 0.1s linear"
        }}>
            <div className="relative rounded-[var(--radius-lg)] overflow-hidden shadow-strong">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1541246/img/therapist_child_session.png" alt="ABA therapist working with child in a caring environment" className="w-full h-auto object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>;
}